import { ref, computed } from "vue";
import { defineStore } from "pinia";
import Ajv, { ValidateFunction } from "ajv";
import { useToast } from "vue-toastification";
import {
  KeyboardConfig,
  Action,
  ActLayer,
  ActDefaultLayer,
  isDefaultLayer,
  isLayer,
  isTrans,
  layersToInternal,
  layersFromInternal,
  ActionInternal,
  KeyboardConfigInternal,
  HoldTapSettings,
  isMultipleActionsInternal,
  isHoldTapInternal,
  configWarnings,
  Warning,
  estimatedConfigSize,
} from "@/keyboard";
import ghanima from "@/keyboard/ghanima.json";
import schema from "@/keyboard/schema.json";

const toast = useToast();

const validateUInt = (val: unknown, bits: number) =>
  Number.isInteger(val) &&
  (val as number) >= 0 &&
  (val as number) <= 2 ** bits - 1;

const validateInt = (val: unknown, bits: number) =>
  Number.isInteger(val) &&
  (val as number) >= -(2 ** bits) &&
  (val as number) <= 2 ** bits - 1;

const addIntFormat = (ajv: Ajv, name: string, bits: number) => {
  const validate = name.startsWith("u") ? validateUInt : validateInt;
  ajv.addFormat(name, {
    type: "number",
    validate: (val: unknown): boolean => validate(val, bits),
  });
};

type Validator = {
  ajv: Ajv;
  validate: ValidateFunction<KeyboardConfig>;
};

const getValidator = (): Validator => {
  const ajv = new Ajv();

  ajv.addFormat("float", { type: "number", validate: (_x: unknown) => true });
  ajv.addFormat("double", { type: "number", validate: (_x: unknown) => true });

  addIntFormat(ajv, "uint8", 8);
  addIntFormat(ajv, "int8", 8);
  addIntFormat(ajv, "uint16", 16);
  addIntFormat(ajv, "int16", 16);
  addIntFormat(ajv, "uint32", 32);
  addIntFormat(ajv, "int32", 32);

  // usize will generate format "uint" which is uint32 in keyboard firmware
  addIntFormat(ajv, "uint", 32);
  addIntFormat(ajv, "int", 32);

  const validate = ajv.compile<KeyboardConfig>(schema);
  return { ajv, validate };
};

const configToInternal = (
  config: KeyboardConfig,
  old_defaults: HoldTapSettings
): { config: KeyboardConfigInternal; defaults: HoldTapSettings } => {
  const { layers, defaults } = layersToInternal(config.layers, old_defaults);
  const internal: KeyboardConfigInternal = {
    layers,
    leds: config.leds,
    mouse: config.mouse,
    timeout: config.timeout,
    bootload_strict: config.bootload_strict,
  };
  return {
    defaults,
    config: internal,
  };
};

export const useKeyboardStore = defineStore("keyboard", () => {
  const validator = getValidator();

  // Validate the default configuration loaded from file
  if (!validator.validate(ghanima)) {
    const error = validator.ajv.errorsText(validator.validate.errors);
    throw Error("Invalid default keyboad config: " + error);
  }

  // Convert the configuration to internal representation
  const c = ghanima as KeyboardConfig;
  const internal = configToInternal(c, {
    timeout: 180,
    config: "Default",
    tap_hold_interval: 0,
  });

  // State variables
  const s = {
    // Geometry would be changed if we loaded config for different firmware
    columns: ref(12),
    rows: ref(5),
    layer: ref(0), // TODO: this should probably live outside of store (in KeysArea)
    ledConfig: ref(0), // as above?
    // Configuration data that can be used to generate a JSON
    config: ref(internal.config),
    // Extracted default values for HoldTap actions
    defaults: ref(internal.defaults),
    // Store copies of original values to determine if config is modified
    original_config: ref(JSON.stringify(internal.config)),
    original_defaults: ref(JSON.stringify(internal.defaults)),
  };

  // --- Getters ---

  const currentLayer = computed((): ActionInternal[][] => {
    return s.config.value.layers[s.layer.value];
  });

  const layerCount = computed((): number => {
    return s.config.value.layers.length;
  });

  const availableLayers = computed((): number[] => {
    return [...Array(layerCount.value).keys()];
  });

  const finalLayers = computed((): Action[][][] => {
    return layersFromInternal(s.config.value.layers, s.defaults.value);
  });

  const modified = computed(
    () =>
      JSON.stringify(s.config.value) != s.original_config.value ||
      JSON.stringify(s.defaults.value) != s.original_defaults.value
  );

  // Components that change the configuration may access state.config directly,
  // but any operations that want to retrieve final config should use this getter.
  const finalConfig = computed((): KeyboardConfig => {
    return {
      layers: finalLayers.value,
      mouse: s.config.value.mouse,
      leds: s.config.value.leds,
      timeout: s.config.value.timeout,
      bootload_strict: s.config.value.bootload_strict,
    };
  });

  const warnings = computed((): Warning[] => configWarnings(s.config.value));

  const getAction = (row: number, col: number): ActionInternal => {
    return s.config.value.layers[s.layer.value][row][col];
  };

  const getActionTransparent = (row: number, col: number): ActionInternal => {
    const act = s.config.value.layers[s.layer.value][row][col];
    return !isTrans(act) ? act : s.config.value.layers[0][row][col];
  };

  // NOTE: from measurements size estimation for default config takes ~0.5 ms
  const getEstimatedConfigSize = () => estimatedConfigSize(s.config.value);

  // --- Actions ---
  const setConfig = (config: object) => {
    if (!validator.validate(config)) {
      const error = validator.ajv.errorsText(validator.validate.errors);
      toast.error("Invalid keyboad config: " + error);
      throw Error("Invalid keyboad config: " + error);
    } else {
      // Convert config to internal representation
      const { layers, defaults } = layersToInternal(
        config.layers,
        s.defaults.value
      );
      const c: KeyboardConfigInternal = {
        layers,
        leds: config.leds,
        mouse: config.mouse,
        timeout: config.timeout,
        bootload_strict: config.bootload_strict,
      };

      // Fix current layer/config
      s.layer.value = Math.min(s.layer.value, c.layers.length - 1);
      s.ledConfig.value = Math.min(s.ledConfig.value, c.leds.length - 1);

      s.config.value = c;
      s.defaults.value = defaults;
      s.original_config.value = JSON.stringify(s.config.value);
      s.original_defaults.value = JSON.stringify(defaults);
    }
  };

  const setAction = (row: number, col: number, action: ActionInternal) => {
    s.config.value.layers[s.layer.value][row][col] = action;
  };

  const setLayer = (layer: number) => {
    if (layer < layerCount.value) {
      s.layer.value = layer;
    }
  };

  // Insert a new layer at given position (use this.layerCount for appending at the end)
  // TODO: update layer references as in removeLayer when inserting layer before last
  const addLayer = (layer: number) => {
    if (layer <= layerCount.value) {
      const newLayer = Array(s.rows.value)
        .fill(null)
        .map((_el) => Array(s.columns.value).fill("Trans"));
      s.config.value.layers.splice(layer, 0, newLayer);
      s.layer.value = layer;
    }
  };

  const removeLayer = (layer: number) => {
    if (layer >= layerCount.value || layerCount.value == 0) return;
    s.config.value.layers.splice(layer, 1);
    if (s.layer.value >= layer && s.layer.value > 0) {
      s.layer.value = s.layer.value - 1;
    }
    s.layer.value = Math.min(s.layer.value, s.config.value.layers.length - 1);

    // Update actions that would now be invalid
    const fix_layer = (
      act: ActLayer | ActDefaultLayer,
      [l, r, c]: [number, number, number]
    ) => {
      const warn = (name: string, from: number, _to: number) =>
        toast.warning(
          `Action ${name}(${from}) at layer ${l}, row ${r}, col ${c} has been invalidated.`
        );

      // Layer < layer -> don't care
      // Layer = layer -> set to 0 + warn
      // Layer > layer -> update to Layer - 1
      if (isLayer(act)) {
        if (act.Layer == layer) {
          warn("Layer", act.Layer, 0);
          act.Layer = 0;
        } else if (act.Layer > layer) {
          act.Layer = act.Layer - 1;
        }
      } else if (isDefaultLayer(act)) {
        if (act.DefaultLayer == layer) {
          warn("DefaultLayer", act.DefaultLayer, 0);
          act.DefaultLayer = 0;
        } else if (act.DefaultLayer > layer) {
          act.DefaultLayer = act.DefaultLayer - 1;
        }
      }
    };

    const fix_action = (act: ActionInternal, at: [number, number, number]) => {
      if (isLayer(act) || isDefaultLayer(act)) {
        fix_layer(act, at);
      } else if (isMultipleActionsInternal(act)) {
        act.MultipleActions.forEach((act) => fix_action(act, at));
      } else if (isHoldTapInternal(act)) {
        fix_action(act.HoldTap.hold, at);
        fix_action(act.HoldTap.tap, at);
      }
    };

    s.config.value.layers.forEach((rows, layer_i) =>
      rows.forEach((cols, row_i) =>
        cols.forEach((act, col_i) => fix_action(act, [layer_i, row_i, col_i]))
      )
    );
  };

  const addLedConfig = (pos: number) => {
    const n = s.config.value.leds.length;
    if (pos < 0 || pos > n) return;
    s.config.value.leds.splice(pos, 0, []);
    s.ledConfig.value = pos;
  };

  const removeLedConfig = (pos: number) => {
    const n = s.config.value.leds.length;
    if (pos < 0 || pos >= n) return;
    s.config.value.leds.splice(pos, 1);
    s.ledConfig.value = Math.min(
      s.ledConfig.value,
      s.config.value.leds.length - 1
    );
  };

  return {
    // State
    ...s,
    // Getters
    modified,
    currentLayer,
    layerCount,
    availableLayers,
    finalLayers,
    finalConfig,
    warnings,
    getAction,
    getActionTransparent,
    getEstimatedConfigSize,
    // Actions
    setConfig,
    setAction,
    setLayer,
    addLayer,
    removeLayer,
    addLedConfig,
    removeLedConfig,
  };
});
